import { alpha, createTheme } from '@mui/material/styles';
import beastTokens from './tokens';

const buildShadows = (tokens) => {
  const shadows = ['none'];
  const levels = [tokens.shadow.xs, tokens.shadow.sm, tokens.shadow.md, tokens.shadow.lg];

  for (let index = 1; index < 25; index += 1) {
    const level = levels[Math.min(Math.floor(index / 6), levels.length - 1)];
    shadows.push(level);
  }

  return shadows;
};

const tokens = beastTokens;

const beastTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: tokens.brand.primary,
      light: tokens.brand.primaryLight,
      dark: tokens.brand.primaryDark,
      contrastText: tokens.neutral[0],
    },
    secondary: {
      main: tokens.brand.secondary,
      light: tokens.brand.secondaryLight,
      dark: tokens.brand.secondaryDark,
      contrastText: tokens.neutral[0],
    },
    success: {
      main: tokens.brand.success,
      contrastText: tokens.neutral[0],
    },
    warning: {
      main: tokens.brand.warning,
      contrastText: tokens.neutral[0],
    },
    error: {
      main: tokens.brand.danger,
      contrastText: tokens.neutral[0],
    },
    info: {
      main: tokens.brand.info,
      contrastText: tokens.neutral[0],
    },
    background: {
      default: tokens.neutral[50],
      paper: tokens.neutral[0],
    },
    text: {
      primary: tokens.neutral[800],
      secondary: tokens.neutral[500],
    },
    grey: {
      50: tokens.neutral[50],
      100: tokens.neutral[100],
      200: tokens.neutral[200],
      300: tokens.neutral[300],
      400: tokens.neutral[400],
      500: tokens.neutral[500],
      600: tokens.neutral[600],
      700: tokens.neutral[700],
      800: tokens.neutral[800],
      900: tokens.neutral[900],
    },
  },
  typography: {
    fontFamily: tokens.fontFamily,
    h1: { fontWeight: 600, letterSpacing: '-0.02em' },
    h2: { fontWeight: 600, letterSpacing: '-0.02em' },
    h3: { fontWeight: 600, letterSpacing: '-0.01em' },
    h4: { fontWeight: 600, letterSpacing: '-0.01em' },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    button: { fontWeight: 600, letterSpacing: '0.01em' },
  },
  shape: {
    borderRadius: tokens.radius.md,
  },
  shadows: buildShadows(tokens),
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          '--beast-primary': tokens.brand.primary,
          '--beast-secondary': tokens.brand.secondary,
          '--beast-surface': tokens.neutral[0],
          '--beast-border': tokens.neutral[200],
          '--beast-text': tokens.neutral[800],
          '--beast-muted': tokens.neutral[500],
        },
        body: {
          margin: 0,
          color: tokens.neutral[800],
          backgroundColor: tokens.neutral[50],
          scrollbarWidth: 'thin',
          scrollbarColor: `${tokens.brand.primary} ${tokens.neutral[100]}`,
          '&::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            background: tokens.neutral[100],
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(tokens.brand.primary, 0.7),
            borderRadius: tokens.radius.sm,
          },
        },
        '*': {
          boxSizing: 'border-box',
        },
        a: {
          color: tokens.brand.primary,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          border: `1px solid ${tokens.neutral[200]}`,
          borderLeft: 'none',
          boxShadow: tokens.shadow.md,
          backgroundColor: tokens.neutral[0],
          borderRadius: 0,
          boxShadow: 'none',
        },
      },
    },
    MuiOutlinedInput: { styleOverrides: { root: { borderRadius: tokens.radius.sm } } },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: tokens.radius.md,
          border: `1px solid ${tokens.neutral[200]}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.md,
          border: `1px solid ${tokens.neutral[200]}`,
          boxShadow: tokens.shadow.xs,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.sm,
          textTransform: 'none',
          padding: '10px 16px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: tokens.shadow.xs,
          },
        },
        outlined: {
          borderColor: tokens.neutral[300],
          '&:hover': {
            borderColor: tokens.brand.primary,
            backgroundColor: alpha(tokens.brand.primary, 0.04),
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.sm,
          fontWeight: 600,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.sm,
          '&.Mui-selected': {
            backgroundColor: alpha(tokens.brand.primary, 0.12),
            color: tokens.brand.primaryDark,
            '& .MuiListItemIcon-root': {
              color: tokens.brand.primary,
            },
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.sm,
          backgroundColor: alpha(tokens.brand.primary, 0.12),
        },
        bar: {
          borderRadius: tokens.radius.sm,
          backgroundColor: tokens.brand.primary,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            fontWeight: 600,
            color: tokens.neutral[700],
            backgroundColor: tokens.neutral[50],
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 10,
          backgroundColor: tokens.neutral[800],
          fontWeight: 600,
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: tokens.shadow.sm,
        },
      },
    },
  },
});

export default beastTheme;
